import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../lib/api';

export interface PlanInfo {
  type: string;
  name: string;
  description: string;
  price: number;
  creditsPerMonth: number;
  checkoutSlug: string;
  features: string[];
}

export interface TopUpInfo {
  slug: string;
  label: string;
  credits: number;
  price: number;
}

export interface SubscriptionInfo {
  id: string;
  planType: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  pendingPlanType: string | null;
}

interface SubscriptionResponse {
  subscription: SubscriptionInfo | null;
  planType: string;
  personalWorkspaceEnabled: boolean;
  plans: PlanInfo[];
  topUps: TopUpInfo[];
}

async function fetchSubscription(): Promise<SubscriptionResponse> {
  const response = await fetch(`${API_URL}/api/v2/cloud/subscription`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription');
  }

  return response.json();
}

export function useSubscription() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: fetchSubscription,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return {
    subscription: data?.subscription ?? null,
    planType: data?.planType ?? 'free',
    personalWorkspaceEnabled: data?.personalWorkspaceEnabled !== false,
    plans: data?.plans ?? [],
    topUps: data?.topUps ?? [],
    isLoading,
    refetch,
  };
}
